import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon.jsx';
import { color, font, radius } from '../../theme/tokens.js';

/** The warm off-white the whole page is written in. */
const CREAM = '#F7F4ED';

/** Stand-ins for the people on the other end. Initials, because there are no faces to use. */
const FRIENDS = ['You', 'K', 'M', 'J'];

/** How long before each further vote lands. The first is already in — it was the swipe. */
const VOTE_MS = 700;

/**
 * What a real swipe earns you.
 *
 * The reel plays itself all day and nothing it does is *yours*. The moment
 * someone commits a yes by hand — button or drag — they have made a decision,
 * and that is the one moment where the second half of the product means
 * anything: you decided, now everyone else has to agree, and that is normally
 * where the evening dies.
 *
 * So the votes arrive after theirs, one at a time, and the group lands on the
 * place they chose. It is a demo of the group feature that costs the visitor
 * nothing to trigger and asks for nothing back.
 *
 * Shown only for a deliberate `right` — not for the timer's, and not for a
 * pass. An automatic reveal every few seconds would be an advert; this is a
 * reply to something the person did.
 *
 * @param {object} props
 * @param {string} props.venueName  What they just said yes to.
 * @param {() => void} props.onDismiss
 */
export function GroupReveal({ venueName, onDismiss }) {
  // Their own vote is already counted; it is the swipe that opened this.
  const [votes, setVotes] = useState(1);
  const decided = votes >= FRIENDS.length;

  useEffect(() => {
    if (decided) return;
    const timer = window.setTimeout(() => setVotes((n) => n + 1), VOTE_MS);
    return () => window.clearTimeout(timer);
  }, [votes, decided]);

  return (
    <div style={styles.wrap} className="fade-in">
      <p style={styles.eyebrow}>{decided ? 'That’s dinner' : 'Nice — that’s your yes'}</p>

      <h2 style={styles.headline}>
        {decided ? (
          <>
            Everyone’s in on <span style={styles.venue}>{venueName}</span>.
          </>
        ) : (
          'Now the part that usually kills it.'
        )}
      </h2>

      <div style={styles.panel}>
        <p style={styles.codeLabel}>Group code</p>
        <p style={styles.code}>CRAVE-7KQ2MX</p>

        <div style={styles.friends}>
          {FRIENDS.map((who, at) => (
            <Friend key={who} label={who} voted={at < votes} />
          ))}
        </div>

        <p style={styles.tally} aria-live="polite">
          {decided ? 'All four said yes.' : `${votes} of ${FRIENDS.length} have voted`}
        </p>
      </div>

      <p style={styles.body}>
        Share the code and everyone swipes on their own phone. The first place you all say yes to
        wins — no group chat, no one having to volunteer a suggestion.
      </p>

      <div style={styles.actions}>
        <a href="/groups" style={styles.primary}>
          See how groups work
        </a>
        <button type="button" onClick={onDismiss} style={styles.secondary}>
          Keep swiping
        </button>
      </div>
    </div>
  );
}

/**
 * One person in the group.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {boolean} props.voted
 */
function Friend({ label, voted }) {
  return (
    <div
      style={{
        ...styles.friend,
        background: voted ? CREAM : 'transparent',
        borderColor: voted ? CREAM : 'rgba(247,244,237,0.32)',
        color: voted ? color.primaryDark : 'rgba(247,244,237,0.55)',
      }}
    >
      {voted ? (
        <Icon name="check" size={15} stroke={color.primaryDark} strokeWidth={3} />
      ) : (
        label.slice(0, 1)
      )}
    </div>
  );
}

const styles = {
  wrap: { textAlign: 'center' },

  eyebrow: {
    margin: 0,
    fontSize: 12,
    fontWeight: font.weight.heavy,
    textTransform: 'uppercase',
    letterSpacing: '1.4px',
    color: 'rgba(244,241,234,0.72)',
  },

  headline: {
    margin: '10px 0 22px',
    fontSize: 'clamp(24px, 6.4vw, 34px)',
    lineHeight: 1.1,
    fontWeight: font.weight.black,
    letterSpacing: '-1px',
    color: CREAM,
    textWrap: 'balance',
  },

  venue: { color: CREAM, whiteSpace: 'nowrap' },

  panel: {
    padding: '18px 18px 16px',
    borderRadius: radius.card,
    background: 'rgba(255,255,255,0.10)',
    border: '1.5px solid rgba(247,244,237,0.24)',
  },

  codeLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: font.weight.heavy,
    textTransform: 'uppercase',
    letterSpacing: '1.3px',
    color: 'rgba(244,241,234,0.62)',
  },

  code: {
    margin: '5px 0 16px',
    fontSize: 26,
    fontWeight: font.weight.black,
    letterSpacing: '2px',
    color: CREAM,
  },

  friends: { display: 'flex', justifyContent: 'center', gap: 10 },
  friend: {
    width: 38,
    height: 38,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderStyle: 'solid',
    fontSize: 14,
    fontWeight: font.weight.bold,
    transition: 'background 0.3s ease, border-color 0.3s ease, color 0.3s ease',
  },

  tally: {
    margin: '12px 0 0',
    fontSize: 12.5,
    fontWeight: font.weight.semibold,
    color: 'rgba(244,241,234,0.78)',
  },

  body: {
    margin: '18px 0 0',
    fontSize: 13.5,
    lineHeight: 1.55,
    color: 'rgba(244,241,234,0.78)',
  },

  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    margin: '16px 0 0',
  },

  primary: {
    padding: '9px 16px',
    borderRadius: radius.pill,
    border: `1.5px solid ${CREAM}`,
    color: CREAM,
    fontSize: 13,
    fontWeight: font.weight.bold,
    textDecoration: 'none',
  },

  secondary: {
    padding: '9px 14px',
    borderRadius: radius.pill,
    border: 'none',
    background: 'transparent',
    color: 'rgba(244,241,234,0.66)',
    fontSize: 13,
    fontWeight: font.weight.semibold,
    cursor: 'pointer',
  },
};
